import { useQuery } from "@tanstack/react-query";
import {
  AreaChart, Area, BarChart, Bar as RBar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/v2/lib/rbac";
import { GlassCard, Kpi, Chip, Skeleton, tip, PageHead } from "@/v2/admin/ui";
import {
  Users, UserCheck, CheckCircle2, Gauge, TrendingUp, Trophy, Sparkles, Activity, Clock, Award,
  Flame, PlayCircle, AlertTriangle, Building2, ArrowUpRight, ArrowDownRight, Info, LayoutDashboard, LogIn, XCircle,
} from "lucide-react";

const TONE_ICON = { up: ArrowUpRight, down: ArrowDownRight, info: Info };

export default function Overview() {
  const { user } = useAuth();
  const canSeeInsights = can(user, "view_reports");
  const { data: s, error, refetch } = useQuery({ queryKey: ["adm-overview"], queryFn: async () => (await api.get("/admin/overview")).data });
  const { data: insights = [] } = useQuery({
    queryKey: ["adm-insights"],
    queryFn: async () => (await api.get("/admin/insights")).data,
    enabled: canSeeInsights,
  });

  if (error) return <GlassCard className="p-5">تعذر تحميل مركز القيادة. <button className="v2-btn-ghost" onClick={() => refetch()}>إعادة المحاولة</button></GlassCard>;
  if (!s) return <Skeleton className="h-96 w-full" />;
  const donut = [{ name: "ناجح", value: s.pass_rate }, { name: "لم يجتز", value: s.fail_rate }];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <PageHead icon={LayoutDashboard} title="مركز القيادة" sub={user.supervision ? `ملخص التدريب ونتائج الاختبارات لموظفي ${user.supervision.unit_name}.` : "نظرة لحظية شاملة على أداء المنصة والموظفين."} />

      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4">
        <Kpi icon={Users} label="إجمالي الموظفين" value={s.total_employees} testId="kpi-employees" />
        <Kpi icon={UserCheck} label="نشطون اليوم" value={s.active_today} />
        <Kpi icon={LogIn} label="تسجيلات الدخول اليوم" value={s.logins_today} tone="gold" />
        <Kpi icon={CheckCircle2} label="دورات مكتملة" value={s.completed_courses} />
        <Kpi icon={Activity} label="محاولات الاختبار" value={s.attempts_count} tone="gold" />
        <Kpi icon={Gauge} label="متوسط الدرجات" value={s.avg_score} suffix="%" />
        <Kpi icon={Trophy} label="نسبة النجاح" value={s.pass_rate} suffix="%" />
        <Kpi icon={XCircle} label="نسبة الرسوب" value={s.fail_rate} suffix="%" tone="red" />
        <Kpi icon={TrendingUp} label="نسبة الإكمال" value={s.completion_rate} suffix="%" />
        <Kpi icon={PlayCircle} label="متوسط مشاهدة الفيديو" value={s.avg_watch_pct} suffix="%" tone="gold" />
        <Kpi icon={Clock} label="ساعات المشاهدة" value={s.hours} tone="gold" />
        <Kpi icon={Award} label="الشهادات الصادرة" value={s.certificates} tone="red" />
      </div>

      <p className="text-xs text-ink-2">{s.courses} دورات منشورة · {s.enrollments} التحاقات · أرقام الاختبارات والمشاهدة تخص تميز.</p>
      <GlassCard gold className="p-5" data-testid="admin-insights" style={canSeeInsights ? undefined : { display: "none" }}>
        <h3 className="font-bold mb-3 flex items-center gap-2"><Sparkles size={16} className="text-beige-ink" /> رؤى المتابعة</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {insights.map((ins, i) => {
            const I = TONE_ICON[ins.tone] || Info;
            const c = ins.tone === "up" ? "bg-ok/15 text-ok" : ins.tone === "down" ? "bg-danger/15 text-danger" : "bg-surface-2 text-ink-2";
            return (
              <div key={i} className="flex items-start gap-3 v2-inset p-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${c}`}><I size={16} /></div>
                <p className="text-sm text-ink/85 leading-relaxed">{ins.text}</p>
              </div>
            );
          })}
          {insights.length === 0 && <p className="text-sm text-ink-2">لا توجد رؤى كافية بعد.</p>}
        </div>
      </GlassCard>

      <div className="grid lg:grid-cols-3 gap-6">
        <GlassCard className="p-5 lg:col-span-2">
          <h3 className="font-bold mb-4">التفاعل خلال آخر 14 يومًا</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={s.series}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#006654" stopOpacity={0.5} /><stop offset="100%" stopColor="#006654" stopOpacity={0} /></linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#bb9661" stopOpacity={0.4} /><stop offset="100%" stopColor="#bb9661" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e3e7ea" vertical={false} />
              <XAxis dataKey="day" tick={{ fill: "#66717a", fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: "#66717a", fontSize: 11 }} />
              <Tooltip {...tip} />
              <Area name="موظفون نشطون" dataKey="learners" stroke="#006654" strokeWidth={2} fill="url(#g1)" />
              <Area name="محاولات" dataKey="attempts" stroke="#bb9661" strokeWidth={2} fill="url(#g2)" />
            </AreaChart>
          </ResponsiveContainer>
        </GlassCard>

        <GlassCard className="p-5">
          <h3 className="font-bold mb-2">النجاح مقابل الرسوب</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={donut} dataKey="value" innerRadius={62} outerRadius={92} paddingAngle={3} stroke="none">
                <Cell fill="#006654" /><Cell fill="#B45309" />
              </Pie>
              <Tooltip {...tip} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 text-xs">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-ok" /> ناجح {s.pass_rate}%</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-danger" /> لم يجتز {s.fail_rate}%</span>
          </div>
        </GlassCard>
      </div>

      {canSeeInsights && <div className="grid lg:grid-cols-2 gap-6">
        <GlassCard className="p-5">
          <h3 className="font-bold mb-4 flex items-center gap-2"><Building2 size={16} className="text-beige-ink" /> أداء الإدارات (نسبة إتمام الدورات)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={s.departments}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e3e7ea" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#66717a", fontSize: 10 }} />
              <YAxis tick={{ fill: "#66717a", fontSize: 11 }} />
              <Tooltip {...tip} />
              <RBar name="نسبة إتمام الدورات" dataKey="completion_rate" fill="#bb9661" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </GlassCard>

        <div className="space-y-6">
          <GlassCard className="p-5">
            <h3 className="font-bold mb-3 flex items-center gap-2"><Trophy size={16} className="text-beige-ink" /> الموظفون الأكثر إتمامًا</h3>
            <ul className="space-y-2">{s.top_employees.map((t, i) => (
              <li key={t.id} className="flex items-center gap-3">
                <span className="w-6 font-bold v2-gold-text">{i + 1}</span>
                <span className="relative w-9 h-9 rounded-full bg-ok/10 text-ok flex items-center justify-center shrink-0">
                  {t.name?.trim().charAt(0)}
                  {t.avatar && <img src={t.avatar} alt="" onError={e => { e.currentTarget.style.display = "none"; }} className="absolute inset-0 w-9 h-9 rounded-full object-cover" />}
                </span>
                <span className="flex-1 font-semibold text-sm">{t.name}</span>
                <span className="font-extrabold v2-emerald-text">{t.completed} دورة</span>
              </li>
            ))}</ul>
          </GlassCard>
          <GlassCard className="p-5">
            <h3 className="font-bold mb-3 flex items-center gap-2"><AlertTriangle size={16} className="text-danger" /> يحتاجون إلى متابعة</h3>
            <ul className="space-y-2">{s.lagging_employees.map((t) => (
              <li key={t.id} className="flex items-center gap-3 text-sm">
                <span className="flex-1 font-semibold">{t.name}</span>
                <Chip tone="muted">{t.enrolled - t.completed} دورات متبقية</Chip>
                <span className="text-ink-2">آخر نشاط: {t.last}</span>
              </li>
            ))}</ul>
          </GlassCard>
        </div>
      </div>}
    </div>
  );
}
